import { canonicalizeType, kindOfType } from './taxonomy'
import type {
  LabeledBox,
  LabelerPrediction,
  LabelerTaxonomy,
  NormalizedBox,
  PageLabels,
} from './types'

/**
 * The labeling working copy: one PageLabels per BQ page id, persisted to
 * localStorage so a reload never loses labels. Nothing here talks to the
 * network.
 */

const STORAGE_PREFIX = 'gecko_labeler_v1'

export type WorkingCopy = Record<string, PageLabels>

function storageKey(siteId: string): string {
  return `${STORAGE_PREFIX}:${siteId}`
}

export function loadWorkingCopy(siteId: string): WorkingCopy {
  try {
    const raw = window.localStorage.getItem(storageKey(siteId))
    if (!raw) return {}
    const parsed = JSON.parse(raw) as { pageLabels?: WorkingCopy }
    return parsed.pageLabels ?? {}
  } catch {
    return {}
  }
}

export function saveWorkingCopy(siteId: string, copy: WorkingCopy): void {
  try {
    window.localStorage.setItem(
      storageKey(siteId),
      JSON.stringify({ pageLabels: copy })
    )
  } catch {
    /* quota/private mode: the in-memory copy still works this session */
  }
}

const now = () => new Date().toISOString()

const newBoxId = () => `box-${Math.random().toString(36).slice(2, 10)}`

export function emptyPage(args: {
  pageId: string
  siteId: string
  documentId: string
  pageNum: number
}): PageLabels {
  return {
    ...args,
    boxes: [],
    rejections: [],
    seedRunId: null,
    complete: false,
    updatedAt: now(),
  }
}

export function predictionBbox(p: LabelerPrediction): NormalizedBox {
  return {
    x_min: Math.min(p.x0, p.x1),
    y_min: Math.min(p.y0, p.y1),
    x_max: Math.max(p.x0, p.x1),
    y_max: Math.max(p.y0, p.y1),
  }
}

function boxIou(a: NormalizedBox, b: NormalizedBox): number {
  const ix = Math.max(
    0,
    Math.min(a.x_max, b.x_max) - Math.max(a.x_min, b.x_min)
  )
  const iy = Math.max(
    0,
    Math.min(a.y_max, b.y_max) - Math.max(a.y_min, b.y_min)
  )
  const inter = ix * iy
  if (inter === 0) return 0
  const areaA = (a.x_max - a.x_min) * (a.y_max - a.y_min)
  const areaB = (b.x_max - b.x_min) * (b.y_max - b.y_min)
  return inter / (areaA + areaB - inter)
}

/**
 * Seed (or re-seed) a page from a source's page slice. Untouched seeds are
 * REPLACED, never appended — refreshing against a newer source must not
 * double the page. Drawn and verified boxes always survive; a prediction
 * they already represent is not re-added — matched by provenance id when
 * present, else by same text + heavy overlap (hydrated boxes and boxes
 * seeded from a different source carry no shared ids).
 */
export function seedFromPredictions(
  page: PageLabels,
  predictions: LabelerPrediction[],
  taxonomy: LabelerTaxonomy | undefined
): PageLabels {
  const kept = page.boxes.filter((b) => b.origin !== 'seeded' || b.verified)
  const keptPredictionIds = new Set(
    kept.map((b) => b.predictionId).filter(Boolean)
  )
  const alreadyRepresented = (p: LabelerPrediction): boolean => {
    if (keptPredictionIds.has(p.id)) return true
    const bbox = predictionBbox(p)
    const text = (p.text ?? '').trim().toLowerCase()
    return kept.some(
      (b) => b.text.trim().toLowerCase() === text && boxIou(b.bbox, bbox) >= 0.6
    )
  }
  const seeded: LabeledBox[] = predictions
    .filter((p) => !alreadyRepresented(p))
    .map((p) => ({
      id: newBoxId(),
      bbox: predictionBbox(p),
      text: p.text ?? '',
      name: p.name,
      type: canonicalizeType(taxonomy, p.type),
      isReference: p.is_reference ?? false,
      verified: false,
      origin: 'seeded',
      predictionId: p.id,
    }))
  return {
    ...page,
    boxes: [...kept, ...seeded],
    seedRunId: predictions[0]?.dagster_run_id ?? page.seedRunId,
    updatedAt: now(),
  }
}

export function addDrawnBox(
  page: PageLabels,
  bbox: NormalizedBox,
  text = ''
): PageLabels {
  const box: LabeledBox = {
    id: newBoxId(),
    bbox,
    text,
    name: null,
    type: null,
    isReference: false,
    verified: true,
    origin: 'drawn',
    predictionId: null,
  }
  return { ...page, boxes: [...page.boxes, box], updatedAt: now() }
}

/** Every edit is a human decision, so edits imply verification. */
export function updateBox(
  page: PageLabels,
  boxId: string,
  updates: Partial<
    Pick<LabeledBox, 'text' | 'name' | 'type' | 'isReference' | 'bbox'>
  >
): PageLabels {
  return {
    ...page,
    boxes: page.boxes.map((b) =>
      b.id === boxId ? { ...b, ...updates, verified: true } : b
    ),
    updatedAt: now(),
  }
}

/** Deleting a seeded box records a rejection — the only direct measure of
 * extractor precision, which the source list cannot express. */
export function deleteBoxes(page: PageLabels, boxIds: Set<string>): PageLabels {
  const rejected = page.boxes.filter(
    (b) => boxIds.has(b.id) && b.predictionId !== null
  )
  const seen = new Set(page.rejections.map((r) => r.predictionId))
  const newRejections = rejected
    .filter((b) => b.predictionId && !seen.has(b.predictionId))
    .map((b) => ({
      predictionId: b.predictionId as string,
      text: b.text || null,
      type: b.type,
      at: now(),
    }))
  return {
    ...page,
    boxes: page.boxes.filter((b) => !boxIds.has(b.id)),
    rejections: [...page.rejections, ...newRejections],
    updatedAt: now(),
  }
}

export function toggleVerified(page: PageLabels, boxId: string): PageLabels {
  return {
    ...page,
    boxes: page.boxes.map((b) =>
      b.id === boxId ? { ...b, verified: !b.verified } : b
    ),
    updatedAt: now(),
  }
}

export function toggleVerifiedMany(
  page: PageLabels,
  boxIds: Set<string>
): PageLabels {
  return {
    ...page,
    boxes: page.boxes.map((b) =>
      boxIds.has(b.id) ? { ...b, verified: !b.verified } : b
    ),
    updatedAt: now(),
  }
}

export function verifyAll(page: PageLabels): PageLabels {
  return {
    ...page,
    boxes: page.boxes.map((b) => (b.verified ? b : { ...b, verified: true })),
    updatedAt: now(),
  }
}

export function bulkSetType(
  page: PageLabels,
  boxIds: Set<string>,
  type: string | null
): PageLabels {
  return {
    ...page,
    boxes: page.boxes.map((b) =>
      boxIds.has(b.id) ? { ...b, type, verified: true } : b
    ),
    updatedAt: now(),
  }
}

export function clearNames(page: PageLabels, boxIds: Set<string>): PageLabels {
  return {
    ...page,
    boxes: page.boxes.map((b) =>
      boxIds.has(b.id) && b.name !== null ? { ...b, name: null } : b
    ),
    updatedAt: now(),
  }
}

export function clearPage(page: PageLabels): PageLabels {
  return {
    ...page,
    boxes: [],
    rejections: [],
    complete: false,
    updatedAt: now(),
  }
}

/** Reading-order sort: top-to-bottom with a small band tolerance, then
 * left-to-right — the same ordering the box list uses. */
export function spatialSort<T extends { bbox: NormalizedBox }>(
  boxes: T[]
): T[] {
  return [...boxes].sort((a, b) => {
    const dy = a.bbox.y_min - b.bbox.y_min
    if (Math.abs(dy) > 0.02) return dy
    return a.bbox.x_min - b.bbox.x_min
  })
}

/**
 * Merge the given boxes into one: texts joined by the delimiter in reading
 * order, bboxes unioned. The merged box keeps the inputs' type only when
 * they all agree; it starts unverified either way, and deleting the
 * originals records rejections for the seeded ones.
 */
export function groupBoxes(
  page: PageLabels,
  boxIds: Set<string>,
  delimiter: string
): PageLabels {
  const members = spatialSort(page.boxes.filter((b) => boxIds.has(b.id)))
  if (members.length < 2) return page
  const types = new Set(members.map((b) => b.type))
  const merged: LabeledBox = {
    id: newBoxId(),
    bbox: {
      x_min: Math.min(...members.map((b) => b.bbox.x_min)),
      y_min: Math.min(...members.map((b) => b.bbox.y_min)),
      x_max: Math.max(...members.map((b) => b.bbox.x_max)),
      y_max: Math.max(...members.map((b) => b.bbox.y_max)),
    },
    text: members.map((b) => b.text).join(delimiter),
    name: null,
    type: types.size === 1 ? members[0].type : null,
    isReference: members.every((b) => b.isReference),
    verified: false,
    origin: 'drawn',
    predictionId: null,
  }
  const deleted = deleteBoxes(page, boxIds)
  return {
    ...deleted,
    boxes: [...deleted.boxes, merged],
    updatedAt: now(),
  }
}

/**
 * Build the `{"records": [...]}` document the manual_labels adapter ingests.
 * One record per (tag surface, kind) — the same grouping key fim-service
 * derives as `source_local_key`, so a surface asserted as two different kinds
 * stays two records and fusion sees the conflict. Boxes with no resolvable
 * kind are dropped: an untyped box is unfinished work, not an assertion.
 * Rejections are deliberately excluded — fusion must never see a rejection.
 */
export function buildRecordsDocument(
  pages: PageLabels[],
  taxonomy: LabelerTaxonomy | undefined
): { records: unknown[]; boxCount: number } {
  interface Group {
    tag: string
    type: string
    kind: string
    occurrences: Array<{
      document_id: string
      page_num: number
      page_id: string
      is_reference: boolean
      bbox: [number, number, number, number]
    }>
    aliases: Set<string>
    verifiedCount: number
    total: number
    isReference: boolean[]
  }
  const groups = new Map<string, Group>()
  let boxCount = 0

  for (const page of pages) {
    for (const box of page.boxes) {
      const kind = kindOfType(taxonomy, box.type)
      if (!kind || !box.type || !box.text.trim()) continue
      boxCount += 1
      const key = `${box.text.trim()} ${kind}`
      let group = groups.get(key)
      if (!group) {
        group = {
          tag: box.text.trim(),
          type: box.type,
          kind,
          occurrences: [],
          aliases: new Set(),
          verifiedCount: 0,
          total: 0,
          isReference: [],
        }
        groups.set(key, group)
      }
      group.total += 1
      if (box.verified) group.verifiedCount += 1
      if (box.name) group.aliases.add(box.name)
      group.isReference.push(box.isReference)
      group.occurrences.push({
        document_id: page.documentId,
        page_num: page.pageNum,
        page_id: page.pageId,
        is_reference: box.isReference,
        bbox: [box.bbox.x_min, box.bbox.y_min, box.bbox.x_max, box.bbox.y_max],
      })
    }
  }

  const records = Array.from(groups.values()).map((g) => ({
    tag: g.tag,
    type: g.type,
    kind: g.kind,
    is_line: g.type === 'pipeline',
    all_reference: g.isReference.length > 0 && g.isReference.every(Boolean),
    confidence: g.total > 0 ? g.verifiedCount / g.total : 0,
    verified: g.verifiedCount === g.total,
    occurrences: g.occurrences,
    aliases: Array.from(g.aliases),
  }))

  return { records, boxCount }
}

/**
 * Snap a hand-drawn rect to the OCR blocks it captures: a block counts when
 * at least half its area falls inside the drawn rect; the box becomes the
 * union of the captured blocks (small padding) and the text prefills in
 * reading order. No captured blocks -> the rect stays exactly as drawn.
 */
export function snapToOcr(
  bbox: NormalizedBox,
  blocks: Array<{
    text: string | null
    x0: number
    y0: number
    x1: number
    y1: number
    reading_order: number | null
  }> = []
): { bbox: NormalizedBox; text: string } {
  const hits = (blocks ?? []).filter((b) => {
    const ix = Math.max(
      0,
      Math.min(bbox.x_max, b.x1) - Math.max(bbox.x_min, b.x0)
    )
    const iy = Math.max(
      0,
      Math.min(bbox.y_max, b.y1) - Math.max(bbox.y_min, b.y0)
    )
    const blockArea = Math.max(1e-9, (b.x1 - b.x0) * (b.y1 - b.y0))
    return (ix * iy) / blockArea >= 0.5
  })
  if (hits.length === 0) return { bbox, text: '' }
  hits.sort((a, b) => (a.reading_order ?? 0) - (b.reading_order ?? 0))
  const pad = 0.0015
  return {
    bbox: {
      x_min: Math.max(0, Math.min(...hits.map((h) => h.x0)) - pad),
      y_min: Math.max(0, Math.min(...hits.map((h) => h.y0)) - pad),
      x_max: Math.min(1, Math.max(...hits.map((h) => h.x1)) + pad),
      y_max: Math.min(1, Math.max(...hits.map((h) => h.y1)) + pad),
    },
    text: hits
      .map((h) => (h.text ?? '').trim())
      .filter(Boolean)
      .join(' '),
  }
}

/** A page carries a human decision — the bar for entering the live list.
 * Pure untouched seeds are the model's own output and stay local. */
export function hasHumanTouch(page: PageLabels): boolean {
  return (
    page.rejections.length > 0 ||
    page.boxes.some((b) => b.origin === 'drawn' || b.verified)
  )
}
